import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Save, 
  RotateCcw,
  AlertCircle,
  Layout,
  Eye,
  EyeOff
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import TemplateSelector from './TemplateSelector';
import { getTemplateById, applyTemplate, getDefaultTemplate } from '../utils/menuTemplates';

/**
 * Customization Component - Simplified Template-Based Theming
 * 
 * STARTER plan: Select from 3 FREE templates (no customization)
 * PRO plan: Select from 13 templates + primary/secondary color customization
 * 
 * Settings saved:
 * - template_id: string (selected template)
 * - custom_primary_color: string (PRO only)
 * - custom_secondary_color: string (PRO only)
 * - layout_mode: string (from template)
 * - enable_item_details: boolean
 */
const CustomizationSimple = () => {
  const { t } = useTranslation();
  const { token, API } = useContext(AuthContext);
  const { tenant, isFeatureEnabled } = useTenant();
  
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Template state
  const [selectedTemplateId, setSelectedTemplateId] = useState('clean-list');
  const [customColors, setCustomColors] = useState({});
  
  // Layout options
  const [enableItemDetails, setEnableItemDetails] = useState(true);
  
  // Track original state for comparison
  const [originalState, setOriginalState] = useState(null);
  
  const plan = tenant?.plan || 'STARTER';
  const isPro = plan === 'PRO' || plan === 'ENTERPRISE';

  // Load current settings from tenant
  useEffect(() => {
    if (tenant?.settings) {
      const settings = tenant.settings;
      
      // Load template ID (default to clean-list)
      const templateId = settings.template_id || 'clean-list';
      setSelectedTemplateId(templateId);
      
      // Load custom colors (PRO only)
      if (isPro) {
        setCustomColors({
          primary: settings.custom_primary_color || '',
          secondary: settings.custom_secondary_color || '',
        });
      }
      
      // Load layout settings
      setEnableItemDetails(settings.enable_item_details ?? true);
      
      // Store original state
      setOriginalState({
        templateId,
        customColors: {
          primary: settings.custom_primary_color || '',
          secondary: settings.custom_secondary_color || '',
        },
        enableItemDetails: settings.enable_item_details ?? true,
      });
      
      // Apply template on load
      const template = getTemplateById(templateId);
      if (template) {
        applyTemplate(template, isPro ? {
          primary: settings.custom_primary_color,
          secondary: settings.custom_secondary_color,
        } : {});
      }
    }
  }, [tenant?.settings, isPro]);

  // Check for unsaved changes
  useEffect(() => {
    if (!originalState) return;
    
    const hasChanges = 
      selectedTemplateId !== originalState.templateId ||
      customColors.primary !== originalState.customColors.primary ||
      customColors.secondary !== originalState.customColors.secondary ||
      enableItemDetails !== originalState.enableItemDetails;
    
    setHasUnsavedChanges(hasChanges);
  }, [selectedTemplateId, customColors, enableItemDetails, originalState]);

  // Handle template change
  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    
    // Apply template immediately for preview
    const template = getTemplateById(templateId);
    if (template) {
      applyTemplate(template, isPro ? customColors : {});
    }
  };

  // Handle color changes (PRO only)
  const handleColorChange = (colors) => {
    setCustomColors(colors);
    
    // Re-apply template with new colors
    const template = getTemplateById(selectedTemplateId);
    if (template) {
      applyTemplate(template, colors);
    }
  };

  // Save settings to backend
  const handleSave = async () => {
    if (!tenant?.id) {
      toast.error(t('messages.tenantInfoNotAvailable') || 'Tenant info not available');
      return;
    }

    setSaving(true);
    try {
      const template = getTemplateById(selectedTemplateId);
      
      const payload = {
        template_id: selectedTemplateId,
        layout_mode: template?.layout?.mode || 'left',
        enable_item_details: enableItemDetails,
      };
      
      // Only include custom colors for PRO
      if (isPro) {
        payload.custom_primary_color = customColors.primary || null;
        payload.custom_secondary_color = customColors.secondary || null;
      }

      await axios.put(
        `${API}/tenant/${tenant.id}/settings`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update original state
      setOriginalState({
        templateId: selectedTemplateId,
        customColors: { ...customColors },
        enableItemDetails,
      });
      
      setHasUnsavedChanges(false);
      toast.success(t('messages.settingsSaved') || 'Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || t('messages.settingsFailed') || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Reset to original state
  const handleReset = () => {
    if (!originalState) return;
    
    setSelectedTemplateId(originalState.templateId);
    setCustomColors(originalState.customColors);
    setEnableItemDetails(originalState.enableItemDetails);
    
    // Re-apply original template
    const template = getTemplateById(originalState.templateId);
    if (template) {
      applyTemplate(template, isPro ? originalState.customColors : {});
    }
    
    toast.info(t('messages.changesDiscarded') || 'Changes discarded');
  };

  return (
    <div className="customization-simple space-y-6">
      {/* Unsaved Changes Banner */}
      {hasUnsavedChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              {t('themes.unsavedChanges') || 'You have unsaved changes'}
            </p>
            <p className="text-xs text-amber-700 mt-1">
              {t('themes.unsavedChangesDescription') || 'Your changes are visible in preview but not published yet.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw size={14} className="mr-1" />
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Save size={14} className="mr-1" />
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </Button>
          </div>
        </div>
      )}

      {/* Template Selector */}
      <Card>
        <CardHeader>
          <CardTitle>{t('templates.menuDesign') || 'Menu Design'}</CardTitle>
          <CardDescription>
            {t('templates.menuDesignDesc') || 'Choose a template that matches your restaurant style'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TemplateSelector
            selectedTemplateId={selectedTemplateId}
            onTemplateChange={handleTemplateChange}
            customColors={customColors}
            onColorChange={handleColorChange}
            showPreviewLink={true}
          />
        </CardContent>
      </Card>

      {/* Additional Layout Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layout size={18} />
            {t('templates.layoutOptions') || 'Layout Options'}
          </CardTitle>
          <CardDescription>
            {t('templates.layoutOptionsDesc') || 'Configure additional display settings'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Item Details Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {enableItemDetails ? <Eye size={20} className="text-gray-600" /> : <EyeOff size={20} className="text-gray-400" />}
              <div>
                <p className="font-medium text-sm">
                  {t('templates.enableItemDetails') || 'Item Detail View'}
                </p>
                <p className="text-xs text-gray-600">
                  {t('templates.enableItemDetailsDesc') || 'Allow customers to tap on items to see more details'}
                </p>
              </div>
            </div>
            <Switch
              checked={enableItemDetails}
              onCheckedChange={setEnableItemDetails}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button (fixed at bottom on mobile) */}
      <div className="sticky bottom-4 flex justify-end gap-3 p-4 bg-white/90 backdrop-blur-sm rounded-lg border shadow-lg md:relative md:bottom-auto md:p-0 md:bg-transparent md:shadow-none md:border-0">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={!hasUnsavedChanges || saving}
        >
          <RotateCcw size={16} className="mr-2" />
          {t('themes.discardChanges') || 'Discard'}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasUnsavedChanges || saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Save size={16} className="mr-2" />
          {saving ? (t('themes.publishing') || 'Saving...') : (t('themes.publishChanges') || 'Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default CustomizationSimple;
